import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtimeEvent } from "../contexts/RealtimeContext";
import { partnerToast } from "../lib/realtimeToast";
import { Layout } from "../components/Layout";
import { AuthImage } from "../components/AuthImage";
import { Heart, Sparkles } from "lucide-react";

export default function Timeline() {
  const { user, partner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/timeline");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
  }, []);

  useRealtimeEvent(
    ["entry:updated", "entry:created", "entry:deleted"],
    (data, actorId, event) => {
      if (actorId && actorId !== user?.id) partnerToast(event, partner?.name);
      load();
    }
  );

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((e) => {
      const d = e.completed_at ? new Date(e.completed_at) : null;
      const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "unbekannt";
      if (!g[key]) g[key] = [];
      g[key].push(e);
    });
    return Object.entries(g).sort(([a], [b]) => b.localeCompare(a));
  }, [items]);

  const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

  return (
    <Layout>
      <header className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-3">Eure Geschichte</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
          Was ihr <span className="italic text-terracotta">erlebt</span> habt.
        </h1>
        <p className="text-text_secondary mt-4 max-w-xl">Eine chronologische Timeline aller Wünsche, die ihr gemeinsam wahr gemacht habt.</p>
      </header>

      {loading ? (
        <div className="text-center py-24 text-text_secondary">Lade Timeline …</div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-bone mx-auto flex items-center justify-center mb-6">
            <Heart size={22} strokeWidth={1.5} className="text-terracotta" />
          </div>
          <h2 className="font-serif text-3xl text-ink mb-3">Eure Geschichte beginnt</h2>
          <p className="text-text_secondary">
            Markiert Einträge als „Erlebt", um sie hier zu sammeln. Eine Erinnerung nach der anderen.
          </p>
        </div>
      ) : (
        <div className="relative pl-8 sm:pl-12">
          {/* vertical line */}
          <div className="absolute left-3 sm:left-5 top-0 bottom-0 w-px bg-border" />
          {grouped.map(([key, entries]) => {
            const [year, month] = key.split("-");
            const label = key === "unbekannt" ? "Ohne Datum" : `${months[parseInt(month, 10) - 1]} ${year}`;
            return (
              <section key={key} className="mb-16" data-testid={`timeline-section-${key}`}>
                <div className="flex items-center gap-4 mb-6 -ml-8 sm:-ml-12">
                  <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-ink text-alabaster flex items-center justify-center">
                    <Sparkles size={12} />
                  </div>
                  <h3 className="font-serif text-2xl text-ink">{label}</h3>
                </div>
                <div className="space-y-4">
                  {entries.map((e) => {
                    const heroId = e.hero_image || (e.images && e.images[0]) || null;
                    return (
                      <div key={e.id} className="bg-white border border-border rounded-2xl overflow-hidden" data-testid={`timeline-item-${e.id}`}>
                        {heroId && (
                          <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-bone" data-testid={`timeline-hero-${e.id}`}>
                            <AuthImage fileId={heroId} size="medium" className="absolute inset-0 w-full h-full object-cover" alt={e.title} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />
                            <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3 text-white">
                              <h4 className="font-serif text-2xl leading-tight drop-shadow-sm">{e.title}</h4>
                              {e.completed_at && (
                                <span className="text-xs opacity-90 whitespace-nowrap">
                                  {new Date(e.completed_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="p-5 sm:p-6">
                          {!heroId && (
                            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                              <h4 className="font-serif text-xl text-ink">{e.title}</h4>
                              {e.completed_at && (
                                <span className="text-xs text-text_secondary">
                                  {new Date(e.completed_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                                </span>
                              )}
                            </div>
                          )}
                          {e.description && <p className="text-sm text-text_secondary">{e.description}</p>}
                          {(e.tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {e.tags.map((t) => (
                                <span key={t} className="text-[10px] uppercase tracking-wider text-text_secondary bg-bone rounded-full px-2 py-0.5">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
